import { Accessor, createSignal } from "solid-js";

import { Fonts, MonospaceFonts } from "@revolt/ui/themes/fonts";
import {
  DEFAULT_KNOT_THEME_ID,
  KNOT_THEMES,
  KnotPalette,
  knotThemeById,
} from "@revolt/ui/themes/knotThemes";

import { State } from "..";

import { AbstractStore } from ".";

export type TypeTheme = {
  /**
   * Base theme preset
   *
   * "knot" — палитры экосистемы Knot (см. ui/themes/knotThemes.ts). Выбран по
   * умолчанию: чат и Knot должны выглядеть одинаково, иначе экосистема
   * распадается на два разных продукта в глазах человека.
   */
  preset: "you" | "knot";

  /**
   * Идентификатор темы Knot, когда preset === "knot".
   */
  knotTheme: string;

  /**
   * Человек выбрал оформление в самом чате.
   *
   * Пока флаг не поднят, тема приезжает из Knot при каждом входе — чат и Knot
   * выглядят одинаково «из коробки». После явного выбора в настройках чата
   * тема становится СОБСТВЕННОЙ и вход из Knot её больше не переписывает:
   * иначе выбранное оформление сбрасывалось при каждом заходе, и человек не мог
   * держать чат в другой теме, чем Knot.
   */
  knotThemeOwn: boolean;

  /**
   * Light/dark mode
   */
  mode: "light" | "dark" | "system";

  /**
   * Accent
   * (Material You)
   */
  m3Accent: string;

  /**
   * Constrast
   * (Material You)
   */
  m3Contrast: number;

  /**
   * Variant
   * (Material You)
   */
  m3Variant:
    | "monochrome"
    | "neutral"
    | "tonal_spot"
    | "vibrant"
    | "expressive"
    | "fidelity"
    | "content"
    | "rainbow"
    | "fruit_salad";

  /**
   * Whether to permit blurry surfaces
   */
  blur: boolean;

  /**
   * Interface font
   */
  interfaceFont: Fonts;

  /**
   * Monospace font
   */
  monospaceFont: MonospaceFonts;

  /**
   * Message size
   */
  messageSize: number;

  /**
   * Spacing between message groups
   */
  messageGroupSpacing: number;
};

type SelectedThemeCommon = Pick & {
  darkMode: boolean;
};

export type SelectedTheme =
  | (SelectedThemeCommon & {
      preset: "you";

      accent: string;
      contrast: number;
      variant: TypeTheme["m3Variant"];
    })
  | (SelectedThemeCommon & {
      preset: "knot";

      knotTheme: string;
      palette: KnotPalette;
    });

/**
 * Manages theme information
 */
export class Theme extends AbstractStore {
  prefersDark: Accessor;

  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "theme");

    // handle prefers-color-scheme value and changes
    const [prefersDark, setPrefersDark] = createSignal(
      window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches,
    );

    this.prefersDark = prefersDark;

    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (event) => setPrefersDark(event.matches));

    this.toggleBlur = this.toggleBlur.bind(this);
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeTheme {
    return {
      preset: "knot",
      knotTheme: DEFAULT_KNOT_THEME_ID,
      knotThemeOwn: false,
      mode: "system",

      m3Accent: "#5470ec",
      m3Contrast: 0.0,
      m3Variant: "tonal_spot",

      interfaceFont: "Inter",
      monospaceFont: "Fira Code",

      blur: true,
      messageSize: 14,
      messageGroupSpacing: 12,
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial): TypeTheme {
    const data: TypeTheme = this.default();

    if (["light", "dark", "system"].includes(input.mode!)) {
      data.mode = input.mode!;
    }

    if (["you", "knot"].includes(input.preset!)) {
      data.preset = input.preset!;
    }

    // Тема из будущей версии (или из чужих настроек) не должна ронять клиент —
    // неизвестный id молча заменяется на тему по умолчанию.
    if (typeof input.knotTheme === "string" && KNOT_THEMES.some((t) => t.id === input.knotTheme)) {
      data.knotTheme = input.knotTheme;
    }

    if (typeof input.knotThemeOwn === "boolean") {
      data.knotThemeOwn = input.knotThemeOwn;
    }

    if (typeof input.m3Contrast === "number") {
      data.m3Contrast = input.m3Contrast;
    }

    if (
      input.m3Accent &&
      input.m3Accent.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})/)
    ) {
      data.m3Accent = input.m3Accent;
    }

    if (
      [
        "monochrome",
        "neutral",
        "tonal_spot",
        "vibrant",
        "expressive",
        "fidelity",
        "content",
        "rainbow",
        "fruit_salad",
      ].includes(input.m3Variant!)
    ) {
      data.m3Variant = input.m3Variant!;
    }

    if (typeof input.blur === "boolean") {
      data.blur = input.blur;
    }

    if (typeof input.messageSize === "number") {
      data.messageSize = input.messageSize;
    }

    if (typeof input.messageGroupSpacing === "number") {
      data.messageGroupSpacing = input.messageGroupSpacing;
    }

    return data;
  }

  /**
   * Get the currently selected theme (considering system settings)
   */
  get activeTheme(): SelectedTheme {
    const opts = this.get();

    switch (opts.preset) {
      case "knot": {
        const theme = knotThemeById(opts.knotTheme);
        return {
          blur: opts.blur,
          interfaceFont: opts.interfaceFont,
          monospaceFont: opts.monospaceFont,
          messageSize: opts.messageSize,
          messageGroupSpacing: opts.messageGroupSpacing,
          preset: "knot",
          // Тема Knot САМА светлая или тёмная — переключатель режима её не
          // касается. Иначе «светлая тема в тёмном режиме» дала бы нечитаемый
          // гибрид из чужих переменных.
          darkMode: theme.kind === "dark",
          knotTheme: theme.id,
          palette: theme.palette,
        };
      }
      case "you":
        return {
          blur: opts.blur,
          interfaceFont: opts.interfaceFont,
          monospaceFont: opts.monospaceFont,
          messageSize: opts.messageSize,
          messageGroupSpacing: opts.messageGroupSpacing,
          preset: "you",
          darkMode:
            opts.mode === "dark" ||
            (opts.mode === "system" && this.prefersDark()),

          accent: opts.m3Accent,
          contrast: opts.m3Contrast,
          variant: opts.m3Variant,
        };
    }
  }

  /**
   * Выбрать тему Knot (заодно переводит пресет на палитры Knot).
   *
   * Явный выбор человека — с этого момента тема чата своя и вход из Knot её не
   * трогает. Тема, приехавшая вместе с кодом входа, ставится не здесь, а
   * `applyKnotThemeFromLogin`.
   */
  setKnotTheme(id: string) {
    this.set("knotTheme", id);
    this.set("preset", "knot");
    this.set("knotThemeOwn", true);
  }

  /**
   * Идентификатор выбранной темы Knot.
   *
   * ⚠️ Читать состояние снаружи можно ТОЛЬКО через такие геттеры: `get()`
   * у AbstractStore защищённый, а метода `getValue` (он есть у стора настроек)
   * здесь нет. Обращение `state.theme.getValue(...)` роняло весь раздел
   * «Внешний вид» — страница настроек открывалась пустой.
   */
  get knotTheme() {
    return this.get().knotTheme;
  }

  /**
   * Тема выбрана в самом чате (а не унаследована от Knot)?
   */
  get knotThemeOwn() {
    return this.get().knotThemeOwn;
  }

  /**
   * Вернуться к теме Knot: следующий вход снова принесёт оформление оттуда.
   */
  followKnotTheme() {
    this.set("knotThemeOwn", false);
  }

  /**
   * Применить тему, приехавшую с кодом входа из Knot.
   *
   * Молча уступает собственному выбору человека — ради этого и заведён флаг.
   * Неизвестный id игнорируем: список тем у чата может отставать от Knot на
   * версию, и подставлять вместо неизвестной темы дефолтную значило бы менять
   * оформление на ровном месте.
   */
  applyKnotThemeFromLogin(id: string) {
    if (this.get().knotThemeOwn) return;
    if (!KNOT_THEMES.some((theme) => theme.id === id)) return;
    this.set("knotTheme", id);
    this.set("preset", "knot");
  }

  /**
   * Get light/dark/system mode
   */
  get mode() {
    return this.get().mode;
  }

  /**
   * Set light/dark/system mode
   * @param mode Mode
   */
  setMode(mode: TypeTheme["mode"]) {
    this.set("mode", mode);
  }

  /**
   * Get current preset
   */
  get preset() {
    return this.get().preset;
  }

  /**
   * Set the active preset
   * @param preset Preset
   */
  setPreset(preset: TypeTheme["preset"]) {
    this.set("preset", preset);
  }

  /**
   * Get current accent
   */
  get m3Accent() {
    return this.get().m3Accent;
  }

  /**
   * Set the accent of the Material You theme
   * @param accent Accent
   */
  setM3Accent(accent: string) {
    this.set("m3Accent", accent);
  }

  /**
   * Get current contrast
   */
  get m3Contrast() {
    return this.get().m3Contrast;
  }

  /**
   * Set the contrast of the Material You theme
   * @param contrast Contrast
   */
  setM3Contrast(contrast: number) {
    this.set("m3Contrast", contrast);
  }

  /**
   * Get current variant
   */
  get m3Variant() {
    return this.get().m3Variant;
  }

  /**
   * Set the variant of the Material You theme
   * @param variant Variant
   */
  setM3Variant(variant: TypeTheme["m3Variant"]) {
    this.set("m3Variant", variant);
  }

  /**
   * Get current blur state
   */
  get blur() {
    return this.get().blur;
  }

  /**
   * Toggle blur state
   */
  toggleBlur() {
    this.set("blur", !this.blur);
  }

  /**
   * Get current interface font
   */
  get interfaceFont() {
    return this.get().interfaceFont;
  }

  /**
   * Set interface font
   */
  setInterfaceFont(font: Fonts) {
    return this.set("interfaceFont", font);
  }

  /**
   * Get current monospace font
   */
  get monospaceFont() {
    return this.get().monospaceFont;
  }

  /**
   * Set monospace font
   */
  setMonospaceFont(font: MonospaceFonts) {
    return this.set("monospaceFont", font);
  }

  /**
   * Get current message size
   */
  get messageSize() {
    return this.get().messageSize;
  }

  /**
   * Set message size
   */
  set messageSize(size: number) {
    this.set("messageSize", size);
  }

  /**
   * Get current message group spacing
   */
  get messageGroupSpacing() {
    return this.get().messageGroupSpacing;
  }

  /**
   * Set message group spacing
   */
  set messageGroupSpacing(space: number) {
    this.set("messageGroupSpacing", space);
  }
}
